import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgForm } from '@angular/forms';
import { UserService } from '../shared/api/resources/user/user.service';
import { User } from '../shared/api/model/user';
import { AgentService } from '../shared/api/resources/agent/agent.service';
import { Agent } from '../shared/api/model/agent';
import { ToasterService}  from '../shared/global/toaster.service';
import { SettingsService } from '../shared/api/resources/settings/settings.service';
import { AccountService } from '../shared/api/resources/account/account.service';
import { UserAccount } from '../shared/api/model/user-account';
import { StaticUtils } from '../shared/utils/static-utils';
import { ImageSelectEvent } from '../shared/atoms/image-upload/image-upload.component';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit {
  @ViewChild('aboutYouForm') aboutYouForm!: NgForm;
  @ViewChild('companyForm') companyForm!: NgForm;
  // TemplateRef for the dialog
  @ViewChild('invalidImageDialog') invalidImageDialog!: TemplateRef<any>;
  // ViewChild to the image upload
  @ViewChild('aboutYouImageUpload') aboutYouImageUpload: any;
  @ViewChild('companyImageUpload') companyImageUpload: any;

  loading: boolean = false;
  user!: User;
  submittingAccount = false;
  submittingAgent = false;
  salesChannelOptions = [
    {
      label: 'Hotel/Concierge',
      value: 200
    },
    {
      label: 'Ticketshop',
      value: 201
    },
    {
      label: 'Tour Operator',
      value: 202
    },
    {
      label: 'Online Travel Agent',
      value: 203
    },
    {
      label: 'Online Activities Website',
      value: 204
    },
    {
      label: 'Local Host',
      value: 205
    },
    {
      label: 'Activities Supplier',
      value: 206
    },
    {
      label: 'Visitor Information Center',
      value: 207
    },
    {
      label: 'Other',
      value: 100
    }
  ];
  ticketTypeOptions = [
    {
      label: 'POS',
      value: 0
    },
    {
      label: 'A4',
      value: 1
    }
  ];
  nationalities: any = [];
  showErrors = false;

  aboutYouImageChanged = false;
  // Full baseline for About You (exclude email)
  aboutYouBaseline: { name: string } = { name: '' };
  companyImageChanged = false;
  companyBaseline: any = {};

  constructor(
    private _userService: UserService,
    private _toasterService: ToasterService,
    private _agentService: AgentService,
    private _accountService: AccountService,
    private _settingsService: SettingsService,
    private _modalService: NgbModal
  ) {}

  ngOnInit() {
    this.loading = true;
    this.user = StaticUtils.deepCopy(this._userService.user);
    this.snapshotAboutYouBaseline();
    this._agentService.getPhysicalAgent().subscribe(
      (agent = Agent) => {
        this.user.userAccountTypeDefault.agent = agent;
        if (!this.user.userAccountTypeDefault.agent.country) {
          this.user.userAccountTypeDefault.agent.country = {
            id: 176,
            code2: 'PT',
            name: 'Portugal',
            dial: '351',
            code: '+351',
            default: true
          };
        }
        this.updateCountryData();
        this.snapshotCompanyBaseline();
      },
      undefined,
      () => this.loading = false
    );
  }

  // Computed revert-aware dirty flag for About You section
  get isAboutYouDirty(): boolean {
    const currentName = (this.user && this.user.name) ? this.user.name.trim() : '';
    const nameChanged = currentName !== this.aboutYouBaseline.name;
    
    return nameChanged || !!this.aboutYouImageChanged;
  }

  get isCompanyDirty(): boolean {
    const agent = this.user && this.user.userAccountTypeDefault && this.user.userAccountTypeDefault.agent ? this.user.userAccountTypeDefault.agent : null;
    
    if (!agent) return !!this.companyImageChanged;

    const currentName = agent.name ? agent.name.trim() : '';
    const currentBrand = agent.brandName ? agent.brandName.trim() : '';
    const currentWebsite = agent.website ? agent.website.trim() : '';
    const currentAddress = agent.address ? agent.address.trim() : '';
    const currentZip = agent.zip ? agent.zip.trim() : '';
    const currentLocation = agent.location ? agent.location.trim() : '';
    const currentPhone = agent.phone ? agent.phone.trim() : '';
    const currentRole = typeof agent.role !== 'undefined' ? agent.role : null;
    const currentTicket = typeof agent.ticketType !== 'undefined' ? agent.ticketType : null;
    const currentCountry = agent.country && (agent.country as any).id ? (agent.country as any).id : null;
    const currentVatNumber = agent.vat ? agent.vat.trim() : '';

    const companyBaseline = this.companyBaseline || {};
    const nameChanged = currentName !== (companyBaseline.name || '');
    const brandChanged = currentBrand !== (companyBaseline.brandName || '');
    const websiteChanged = currentWebsite !== (companyBaseline.website || '');
    const addressChanged = currentAddress !== (companyBaseline.address || '');
    const zipChanged = currentZip !== (companyBaseline.zip || '');
    const locationChanged = currentLocation !== (companyBaseline.location || '');
    const phoneChanged = currentPhone !== (companyBaseline.phone || '');
    const roleChanged = currentRole !== (typeof companyBaseline.role !== 'undefined' ? companyBaseline.role : null);
    const ticketChanged = currentTicket !== (typeof companyBaseline.ticketType !== 'undefined' ? companyBaseline.ticketType : null);
    const countryChanged = currentCountry !== (typeof companyBaseline.countryId !== 'undefined' ? companyBaseline.countryId : null);
    const vatChanged = currentVatNumber !== (companyBaseline.vat || '');

    return nameChanged || brandChanged || websiteChanged || addressChanged || zipChanged || locationChanged || phoneChanged || roleChanged || ticketChanged || countryChanged || !!this.companyImageChanged || vatChanged ;
  }

  // snapshot helpers
  private snapshotAboutYouBaseline() {
    this.aboutYouBaseline = {
      name: (this.user && this.user.name) ? this.user.name.trim() : ''
    };
  }

  private snapshotCompanyBaseline() {
    const agent = this.user && this.user.userAccountTypeDefault && this.user.userAccountTypeDefault.agent ? this.user.userAccountTypeDefault.agent : null;
    
    if (!agent) {
      this.companyBaseline = {};
      
      return;
    }

    this.companyBaseline = {
      name: agent.name ? agent.name.trim() : '',
      brandName: agent.brandName ? agent.brandName.trim() : '',
      website: agent.website ? agent.website.trim() : '',
      address: agent.address ? agent.address.trim() : '',
      zip: agent.zip ? agent.zip.trim() : '',
      location: agent.location ? agent.location.trim() : '',
      phone: agent.phone ? agent.phone.trim() : '',
      vat: agent.vat ? agent.vat.trim() : '',   
      // Include dropdowns in baseline
      role: typeof agent.role !== 'undefined' ? agent.role : null,
      ticketType: typeof agent.ticketType !== 'undefined' ? agent.ticketType : null,
      countryId: agent.country && (agent.country as any).id ? (agent.country as any).id : null
    };
  }

  onCountrySelect(country: any) {
    this.user.userAccountTypeDefault.agent.country = country;
  }
  
  public updateCountryData(): void {
    this.nationalities = [];
    const topCodes = ['PT', 'GB', 'US', 'ES', 'FR'];
    const topCountries = this._settingsService.countries.filter(c => topCodes.includes(c.code2)).map(country => {
      return {
        label: country.name,
        value: country
      }
    });
    const allCountries = this._settingsService.countries.map(country => {
      return {
        label: country.name,
        value: country
      }
    });

    this.nationalities = [
        {
            groupLabel: 'Top 5 - Countries',
            items: topCountries
        },
        {
            groupLabel: 'All Countries',
            items: allCountries
        }
    ];
  }

  private computeImageChanged(evt: ImageSelectEvent, uploadRef: any): boolean {
    return !!evt.changed || (uploadRef && uploadRef.isImageValid === false);
  }

  onAboutYouImageSelected(evt: ImageSelectEvent) {
    if (evt.base64) this.user.imagePhotoBase64 = evt.base64;
    this.aboutYouImageChanged = this.computeImageChanged(evt, this.aboutYouImageUpload);
  }

  onCompanyImageSelected(evt: ImageSelectEvent) {
    if (evt.base64) (this.user.userAccountTypeDefault.agent as any).imageLogoBase64 = evt.base64;
    this.companyImageChanged = this.computeImageChanged(evt, this.companyImageUpload);
  }

  submitYourCompany() {
    // Guard: show dialog if About You image has validation errors
    const hasInvalidImage = this.aboutYouImageUpload && this.aboutYouImageUpload.isImageValid === false;

    if (hasInvalidImage) {
      this._modalService.open(this.invalidImageDialog, {
        size: 'sm', centered: true, windowClass: 'invalid-image-modal', backdrop: 'static', keyboard: false
      });

      return;
    }

    if (this.isAccountValid()) {
      this.submittingAccount = true;
      this._accountService.userAccountEdit(this.user).subscribe(
        (user: UserAccount) => {
          this._userService.user = user;
          this._userService.userAccount = user;
          this._toasterService.showInfo('Account updated successfully.');
          // Refresh local model 
          this.user = StaticUtils.deepCopy(this._userService.user as any);
          // reset change tracking after successful save
          this.aboutYouImageChanged = false;
          // reset baseline
          this.snapshotAboutYouBaseline();
          // sync uploader baseline (hash original) so re-uploading an older file is compared to new image
          setTimeout(() => {
            if (this.aboutYouImageUpload && this.aboutYouImageUpload.refreshOriginalHash) {
              try { this.aboutYouImageUpload.refreshOriginalHash(); } catch {}
            }
          });

          if (this.aboutYouForm) {
            this.aboutYouForm.form.markAsPristine();
          }
        },
        undefined,
        () => (this.submittingAccount = false)
      );
    } else {
      this.showErrors = true;
    }
  }

  submitAboutYou() {
    // Guard: block save and show modal if Company image is invalid
    const hasInvalidCompanyImage = this.companyImageUpload && this.companyImageUpload.isImageValid === false;

    if (hasInvalidCompanyImage) {
      this._modalService.open(this.invalidImageDialog, {
        size: 'sm', centered: true, windowClass: 'invalid-image-modal', backdrop: 'static', keyboard: false
      });

      return;
    }

    if (this.isAgentValid()) {
      this.submittingAgent = true;
      this._agentService.updateProfile(this.user.userAccountTypeDefault.agent).subscribe(
        (agent: Agent) => {
          this._userService.user.userAccountTypeDefault.agent = agent;
          this._userService.userAccount.userAccountTypeDefault.agent = agent;
          this._toasterService.showInfo('Company updated successfully.');
          this.user.userAccountTypeDefault.agent = agent;
          // reset change tracking after successful save
          this.companyImageChanged = false;
          // reset company baselines so reverting to saved values disables Save
          this.snapshotCompanyBaseline();
          // sync uploader baseline (hash original) so re-uploading an older file is compared to new image
          setTimeout(() => {
            if (this.companyImageUpload && this.companyImageUpload.refreshOriginalHash) {
              try { this.companyImageUpload.refreshOriginalHash(); } catch {}
            }
          });

          if (this.companyForm) {
            this.companyForm.form.markAsPristine();
          }
        },
        undefined,
        () => this.submittingAgent = false
      );
    } else {
      this.showErrors = true;
    }
  }

  getSalesChannelLabel(value: any): string {
    let result = '';

    for (const option of this.salesChannelOptions) {
      if (option.value === value) {
        result = option.label;
      }
    }

    return result;
  }

  countryString(val: any) {
    const flatItems: any[] = this.nationalities.reduce((acc: any, cur: any) => {
      acc.push(...cur.items.map((item: any) => item.value));
      
      return acc;
    }, []);

    // Filter country
    const country: any = flatItems.find((n: any) => n.id === val.id);
    
    return country.name;
  }

  getTicketTypeLabel(value: number): string {
    let result = '';
    
    switch (value) {
      case 0:
        result = 'POS';
        break;
      case 1:
        result = 'A4';
        break;
      default:
        break;
    }

    return result;
  }

  isAccountValid(): boolean {
    return !this.isNameEmpty() && this.isNameValid();
  }

  isAgentValid(): boolean {
    return !this.isCompanyNameEmpty() && this.isCompanyNameValid()
      && !this.isBrandNameEmpty() && this.isBrandNameValid()
      && !this.isAddressEmpty()
      && !this.isZipCodeEmpty()
      && !this.isLocationEmpty()
      && !this.isCountryEmpty()
      && !this.isPhoneEmpty() && this.isPhoneValid();
  }

  isNameEmpty(): boolean {
    return !this.user.name || this.user.name === '';
  }

  isNameValid(): boolean {
    return !this.user.name || this.user.name.length >= 3;
  }

  isBrandNameEmpty(): boolean {
    return !this.user.userAccountTypeDefault.agent.brandName || this.user.userAccountTypeDefault.agent.brandName === '';
  }

  isBrandNameValid(): boolean {
    return !this.user.userAccountTypeDefault.agent.brandName || this.user.userAccountTypeDefault.agent.brandName.length >= 3;
  }

  isCompanyNameEmpty(): boolean {
    return !this.user.userAccountTypeDefault.agent.name || this.user.userAccountTypeDefault.agent.name === '';
  }

  isCompanyNameValid(): boolean {
    return !this.user.userAccountTypeDefault.agent.name || this.user.userAccountTypeDefault.agent.name.length >= 3;
  }

  isAddressEmpty(): boolean {
    return !this.user.userAccountTypeDefault.agent.address || this.user.userAccountTypeDefault.agent.address === '';
  }

  isZipCodeEmpty(): boolean {
    return !this.user.userAccountTypeDefault.agent.zip || this.user.userAccountTypeDefault.agent.zip === '';
  }

  isLocationEmpty(): boolean {
    return !this.user.userAccountTypeDefault.agent.location || this.user.userAccountTypeDefault.agent.location === '';
  }

  isCountryEmpty(): boolean {
    return this.user.userAccountTypeDefault.agent.country === null || this.user.userAccountTypeDefault.agent.country === undefined;
  }

  isPhoneEmpty(): boolean {
    return !this.user.userAccountTypeDefault.agent.phone || this.user.userAccountTypeDefault.agent.phone === '';
  }

  isPhoneValid(): boolean {
    return !this.user.userAccountTypeDefault.agent.phone ||
      (!this.user.userAccountTypeDefault.agent.phone.match(/[a-z]/i) && this.user.userAccountTypeDefault.agent.phone.length >= 9);
  }
}



// WEBPACK FOOTER //
// ./src/app/user-profile/user-profile.component.ts